import { faDoorOpen, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, Pagination, type SelectOption, TextField, Title } from '@tryhackme/thm-ui-components';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { ReactComponent as SubmissionsIcon } from 'src/images/svgs/submissions.svg';
import { SubmissionsTable } from './components/table/submissions-table';
import { SelectOptions, SelectedStatus, defaultOption, defaultSubmissionsOption, defaultTesterOption } from './submissions.constants';
import { useGetSubmissionsQuery, useGetUniqueTestersQuery } from './submissions.slice';
import { HeadingWrapper, PagginationWrapper, StyledBanner, StyledBox, StyledContainer, StyledDropDown, StyledFilters, StyledHeading, StyledHeadingIcon, StyledIcon, StyledLimitSelect, StyledNoRecordMessage, StyledPagination, StyledSearch, StyledSelect, StyledSize, StyledTesterContainer, StyledText } from './submissions.styles';
import { type SubmissionsParams, SubmissionsSortFieldTypes, type SubmissionsTableType, type TestersData } from './submissions.types';
export const Submissions = () => {
  const [submissionsParams, setSubmissionsParams] = useState<SubmissionsParams>({
    page: 1,
    limit: 10,
    search: '',
    sort: `-${SubmissionsSortFieldTypes.REQUESTED}`,
    status: '',
    tester: ''
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<SelectOption>(defaultOption);
  const [selectedTesters, setSelectedTesters] = useState<SelectOption>(defaultTesterOption);
  const [selectedStatus, setSelectedStatus] = useState<SelectOption>(defaultSubmissionsOption);
  const [activeSortColumn, setActiveSortColumn] = useState<SubmissionsTableType['activeSortColumn']>('requested');
  const {
    data: testers
  } = useGetUniqueTestersQuery();
  const {
    data: submissions,
    isLoading
  } = useGetSubmissionsQuery(submissionsParams);
  const numberOfRecords = {
    total: submissions?.data?.totalDocs ?? 0,
    fetched: submissions?.data?.docs?.length ?? 0
  };

  // Handle filter data
  const handleSelection = (option: SelectOption = defaultOption) => {
    setSelectedOption(() => ({
      value: option.value,
      label: option.label,
      id: option.id
    }));
    setSubmissionsParams(prevParams => ({
      ...prevParams,
      limit: Number.parseInt(option.value, 10),
      page: 1
    }));
  };
  const handleStatusSelection = (option: SelectOption = defaultTesterOption) => {
    setSelectedStatus(() => ({
      value: option.value,
      label: option.label,
      id: option.id
    }));
    setSubmissionsParams(prevParams => ({
      ...prevParams,
      status: option.value,
      page: 1
    }));
  };
  const handleTesterSelection = (option: SelectOption = defaultSubmissionsOption) => {
    setSelectedTesters(() => ({
      value: option.value,
      label: option.label,
      id: option.id
    }));
    setSubmissionsParams(prevParams => ({
      ...prevParams,
      tester: option.id,
      page: 1
    }));
  };
  const handlePageChange = (pageNumber: number) => {
    setSubmissionsParams(prevParams => ({
      ...prevParams,
      page: pageNumber
    }));
  };
  const handleTextChange = useDebouncedCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSubmissionsParams(prevParams => ({
      ...prevParams,
      search: e.target.value,
      page: 1
    }));
  }, 800);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    handleTextChange(e);
  };

  // Import testers to select component
  const selectionTesters: SelectOption[] = testers?.data?.length ? [{
    id: '',
    value: '',
    label: 'All Testers'
  }, ...testers.data.filter((tester: TestersData) => tester?.username).map((tester: TestersData) => ({
    id: tester.userId,
    value: tester.username,
    label: tester.username
  }))] : [{
    id: '',
    value: '',
    label: 'All Testers'
  }];
  return <section data-sentry-component="Submissions" data-sentry-source-file="submissions.tsx">
      <StyledBanner Image={SubmissionsIcon} data-sentry-element="StyledBanner" data-sentry-source-file="submissions.tsx">
        <Container data-sentry-element="Container" data-sentry-source-file="submissions.tsx">
          <Title isInBanner titleText="Submissions" subTitleText="Submitted rooms awaiting evaluation" data-sentry-element="Title" data-sentry-source-file="submissions.tsx" />
        </Container>
      </StyledBanner>
      <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="submissions.tsx">
        <HeadingWrapper data-sentry-element="HeadingWrapper" data-sentry-source-file="submissions.tsx">
          <StyledBox data-sentry-element="StyledBox" data-sentry-source-file="submissions.tsx">
            <StyledIcon data-sentry-element="StyledIcon" data-sentry-source-file="submissions.tsx">
              <StyledHeadingIcon icon={faDoorOpen} data-sentry-element="StyledHeadingIcon" data-sentry-source-file="submissions.tsx" />
            </StyledIcon>
            <StyledHeading data-sentry-element="StyledHeading" data-sentry-source-file="submissions.tsx">Submissions</StyledHeading>
          </StyledBox>
          <StyledTesterContainer data-sentry-element="StyledTesterContainer" data-sentry-source-file="submissions.tsx">
            <StyledSize data-sentry-element="StyledSize" data-sentry-source-file="submissions.tsx">Display</StyledSize>
            <StyledSelect data-sentry-element="StyledSelect" data-sentry-source-file="submissions.tsx">
              <TextField isMulti={false} isSearchable={false} variant="select" handleSelection={handleTesterSelection} options={selectionTesters} selected={selectedTesters} defaultValue={selectionTesters[0]} data-sentry-element="TextField" data-sentry-source-file="submissions.tsx" />
            </StyledSelect>
          </StyledTesterContainer>
          <StyledSelect data-sentry-element="StyledSelect" data-sentry-source-file="submissions.tsx">
            <TextField isMulti={false} isSearchable={false} variant="select" handleSelection={handleStatusSelection} options={SelectedStatus} selected={selectedStatus} defaultValue={SelectedStatus[0]} data-sentry-element="TextField" data-sentry-source-file="submissions.tsx" />
          </StyledSelect>
        </HeadingWrapper>
        <StyledFilters data-sentry-element="StyledFilters" data-sentry-source-file="submissions.tsx">
          <StyledSearch data-sentry-element="StyledSearch" data-sentry-source-file="submissions.tsx">
            <TextField placeholder="Search for room submissions" variant="input" leftAdornment={<FontAwesomeIcon icon={faSearch} />} onChange={handleInputChange} value={searchValue} data-sentry-element="TextField" data-sentry-source-file="submissions.tsx" />
          </StyledSearch>
          <StyledDropDown data-sentry-element="StyledDropDown" data-sentry-source-file="submissions.tsx">
            <StyledSize data-sentry-element="StyledSize" data-sentry-source-file="submissions.tsx">Show</StyledSize>
            <StyledLimitSelect data-sentry-element="StyledLimitSelect" data-sentry-source-file="submissions.tsx">
              <TextField isMulti={false} isSearchable={false} variant="select" handleSelection={handleSelection} options={SelectOptions} selected={selectedOption} defaultValue={SelectOptions[1]} data-sentry-element="TextField" data-sentry-source-file="submissions.tsx" />
            </StyledLimitSelect>
            <StyledSize data-sentry-element="StyledSize" data-sentry-source-file="submissions.tsx">records</StyledSize>
          </StyledDropDown>
        </StyledFilters>
        <SubmissionsTable submissions={submissions} isLoading={isLoading} activeSortColumn={activeSortColumn} setActiveSortColumn={setActiveSortColumn} submissionsParams={submissionsParams} setSubmissionsParams={setSubmissionsParams} data-sentry-element="SubmissionsTable" data-sentry-source-file="submissions.tsx" />
        {numberOfRecords.fetched === 0 && <StyledNoRecordMessage>No submissions found</StyledNoRecordMessage>}
        <PagginationWrapper data-sentry-element="PagginationWrapper" data-sentry-source-file="submissions.tsx">
          <StyledText data-sentry-element="StyledText" data-sentry-source-file="submissions.tsx">
            <p>
              Showing{' '}
              {numberOfRecords.fetched > 0 ? `${(submissionsParams.page - 1) * submissionsParams.limit + 1} to ` : 0}
              {numberOfRecords.fetched > 0 ? `${submissionsParams.page * submissionsParams.limit} ` : ` `}
              of {numberOfRecords.total} records
            </p>
          </StyledText>
          {numberOfRecords.fetched > 0 && <StyledPagination>
              <Pagination onChange={handlePageChange} selectedPage={submissionsParams.page} totalPages={Math.ceil(numberOfRecords.total / submissionsParams.limit)} />
            </StyledPagination>}
        </PagginationWrapper>
      </StyledContainer>
    </section>;
};